import type { SnackbarOrigin } from '@mui/material/Snackbar';
import { useCallback, useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import useAlertStore from '@/stores/alertStore';
import { noteGoApiFetch } from '@/services/noteGoApi/noteGoApiFetcher';
import { logger } from '@/services/loggerService';
import { passesVersionGate } from '@/utils/versionGate';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes — matches backend TTL cache
const SESSION_KEY = 'notego_campaign_shown_ids';
// Campaign toasts anchor bottom-right; every other alertStore caller stays
// top-center (the store default).
const CAMPAIGN_TOAST_POSITION: SnackbarOrigin = { vertical: 'bottom', horizontal: 'right' };

interface CampaignMessage {
  id: string;
  title: string;
  body: string;
  severity: 'info' | 'success' | 'warning' | 'error';
  linkUrl: string | null;
  delivery?: string[];
  /**
   * Desktop version this campaign announces, e.g. "1.2.48". The API passes it
   * through verbatim and never filters on it — the client owns the comparison.
   * Absent means the campaign is not version-gated.
   */
  releaseVersion?: string | null;
}

interface MessagesResponse {
  messages: CampaignMessage[];
}

function getShownIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set<string>();
  }
}

function markShown(id: string): void {
  try {
    const ids = getShownIds();
    ids.add(id);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...ids]));
  } catch {
    // sessionStorage may be unavailable (private-browsing fallback)
  }
}

/**
 * Polls `GET /api/messages/active?platform=web` on mount and every 5 minutes.
 * Shows each new campaign as a PERSISTENT toast (no auto-hide) via
 * alertStore.showAlert() — it stays on screen until the user dismisses it.
 * Queued campaigns are shown one at a time; the next is shown only after the
 * current one is dismissed. Renders nothing — side-effect component.
 *
 * After a toast is actually rendered (only while the tab is visible), acks it
 * via `POST /api/messages/seen` so the server marks it seen for this user and
 * drops it from the feed on every platform — the server feed is now the source
 * of truth for read state; the sessionStorage set is only an in-session guard.
 */
const CampaignToastController = () => {
  const showAlert = useAlertStore((state) => state.showAlert);
  const alertVisible = useAlertStore((state) => state.visible);
  const userToken = useAuthStore((state) => state.userToken);
  const userData = useAuthStore((state) => state.userData);
  // Null for the many web users with no desktop app at all, which leaves
  // version-gated campaigns showing rather than hidden (see passesVersionGate).
  const desktopVersion = userData?.desktopVersion ?? null;
  const queueRef = useRef<CampaignMessage[]>([]);
  const showAlertRef = useRef(showAlert);
  showAlertRef.current = showAlert;
  const visibleRef = useRef(alertVisible);
  const showNextRef = useRef<() => void>(() => {});
  // Holds a pending one-shot visibilitychange listener so it can be torn down
  // on unmount (see the poll effect cleanup below).
  const visibilityHandlerRef = useRef<(() => void) | null>(null);

  // Tell the server this campaign was actually rendered so it is filtered from
  // the feed on every platform. Fire-and-forget: called AFTER rendering and
  // never awaited, so it can never gate or delay the toast. Back-compat: if the
  // staging API hasn't shipped `/api/messages/seen` yet it 404s — log and
  // continue, never retry-loop.
  const ackSeen = useCallback((id: string) => {
    noteGoApiFetch({
      url: '/api/messages/seen',
      method: 'POST',
      body: { platform: 'web', ids: [id] },
    }).catch((err) => {
      logger.warn('api', 'Failed to ack seen campaign', err);
    });
  }, []);

  const showNext = useCallback(() => {
    if (queueRef.current.length === 0) return;
    // Visibility guard: don't burn a campaign's one impression on a hidden
    // background tab. Defer rendering until the tab is visible again.
    if (document.visibilityState !== 'visible') {
      if (!visibilityHandlerRef.current) {
        const onVisible = () => {
          if (document.visibilityState !== 'visible') return;
          document.removeEventListener('visibilitychange', onVisible);
          visibilityHandlerRef.current = null;
          showNextRef.current();
        };
        visibilityHandlerRef.current = onVisible;
        document.addEventListener('visibilitychange', onVisible);
      }
      return;
    }
    const msg = queueRef.current.shift()!;
    markShown(msg.id);
    // `null` duration => the toast never auto-hides; it persists until the user
    // dismisses it via the close (X) or View button (see Alert.tsx).
    showAlertRef.current(msg.body, msg.severity, null, msg.linkUrl ?? null, CAMPAIGN_TOAST_POSITION);
    // Ack only what we just rendered — never ids we didn't show. Dismissal is a
    // purely local action and does NOT mark seen; rendering does.
    ackSeen(msg.id);
  }, [ackSeen]); // ackSeen is stable; other mutable state accessed via refs
  showNextRef.current = showNext;

  const fetchAndQueue = useCallback(async () => {
    try {
      const result = (await noteGoApiFetch({
        url: '/api/messages/active',
        method: 'GET',
        queryParams: { platform: 'web' },
      })) as MessagesResponse;
      const shownIds = getShownIds();
      const newMsgs = result.messages.filter((m) => !shownIds.has(m.id));
      if (newMsgs.length === 0) return;
      // Only in-app toasts belong in the queue. Back-compat: the currently-
      // deployed staging API omits `delivery`, so treat a missing value as
      // ['toast'] — never blank out toasts. Push-only campaigns are marked shown
      // so they don't re-queue every poll.
      const toastMsgs: CampaignMessage[] = [];
      for (const m of newMsgs) {
        // Optional release-version gate. A campaign announcing a desktop build
        // this user is already on is dropped WITHOUT acking it seen: the ack
        // means "actually rendered", and a suppressed campaign never rendered.
        // It is deliberately not marked shown locally either, so the gate
        // re-evaluates on the next poll once a desktop version is known.
        if (!passesVersionGate(m.releaseVersion, desktopVersion)) continue;

        if ((m.delivery ?? ['toast']).includes('toast')) {
          toastMsgs.push(m);
        } else {
          markShown(m.id);
        }
      }
      if (toastMsgs.length === 0) return;
      queueRef.current.push(...toastMsgs);
      // Only start showing if no toast is currently on screen; otherwise the
      // visible one stays until dismissed and the queue advances on close.
      if (!visibleRef.current) showNext();
    } catch (err) {
      logger.warn('api', 'Failed to fetch campaign messages', err);
    }
  }, [showNext, desktopVersion]);

  // When the current campaign toast is dismissed (visible: true -> false),
  // show the next queued campaign, if any.
  useEffect(() => {
    const wasVisible = visibleRef.current;
    visibleRef.current = alertVisible;
    if (wasVisible && !alertVisible && queueRef.current.length > 0) {
      showNext();
    }
  }, [alertVisible, showNext]);

  useEffect(() => {
    if (!userToken || !userData) return;

    fetchAndQueue();
    const pollInterval = setInterval(fetchAndQueue, POLL_INTERVAL_MS);

    return () => {
      clearInterval(pollInterval);
      if (visibilityHandlerRef.current) {
        document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
        visibilityHandlerRef.current = null;
      }
    };
  }, [userToken, userData, fetchAndQueue]);

  return null;
};

export default CampaignToastController;
